"use client";

import styles from "@/styles/product-detail/addon-deals.module.scss";
import Image from "next/image";
import Link from "next/link";
import { useCallback, useEffect, useMemo, useState } from "react";
import Dialog from "@/components/global/Dialog";
import AddOnInfoContent from "@/components/product-detail/AddOnInfoContent";
import AddOnProductList from "@/components/product-detail/AddOnProductList";
import addOnApi from "@/service/modules/addOn";
import useCartActions from "@/utils/addToCartAction";
import useAddOnCartItems from "@/utils/useAddOnCartItems";
import { AddOnProduct } from "@/types/product";
import { useProductQty } from "@/components/product-detail/ProductQtyContext";
import { toast } from "@/components/global/Toast";

interface Props {
  productId: number | string;
  source: "detail" | "cart";
  price?: number;
  subtotal?: number;
  onAddOnSuccess?: (response?: unknown) => void;
  onAvailabilityChange?: (hasProduct: boolean) => void;
}

const getImage = (item: { newThumbnail?: string; filename?: string; thumbnail?: string }) =>
  item.newThumbnail || item.thumbnail || item.filename || "";

const normalizeProduct = (item: Record<string, any>): AddOnProduct => {
  const normalized = {
    ...item,
    price: Number(item.addonPrice || item.price),
    normalPrice: Number(item.normalPrice || item.originalPrice),
  };
  return normalized as AddOnProduct;
};

export default function AddonDeals({
  productId,
  source,
  price,
  subtotal,
  onAddOnSuccess,
  onAvailabilityChange,
}: Props) {
  const { qty } = useProductQty();
  const computedSubtotal =
    source === "detail" && price != null ? price * qty : subtotal;
  const [product, setProduct] = useState<AddOnProduct | null>(null);
  const [added, setAdded] = useState(false);
  const [adding, setAdding] = useState(false);
  const [showInfoDialog, setShowInfoDialog] = useState(false);
  const [showProductList, setShowProductList] = useState(false);
  const { addToCart, batchAddToCart } = useCartActions();
  const mainProduct = useMemo(
    () =>
      source === "detail" && productId
        ? { productID: productId, qty }
        : undefined,
    [productId, qty, source]
  );
  const getAddOnCartItems = useAddOnCartItems(mainProduct);

  const fetchAddOns = useCallback(async () => {
    try {
      const redZoneParams: Record<string, any> = { perPage: 1, page: 1 };
      if (computedSubtotal !== undefined) {
        redZoneParams.subtotal = computedSubtotal;
      }
      const res = await addOnApi.getAddOnRedZone(redZoneParams);
      const list =
        res?.data?.list || res?.data?.items || res?.data || [];
      const normalized = Array.isArray(list)
        ? list
            .map((item: Record<string, any>) => normalizeProduct(item))
            .filter((item: AddOnProduct) => item.productID && getImage(item))
        : [];

      if (normalized.length) {
        setProduct(normalized[0]);
        onAvailabilityChange?.(true);
        return;
      }

      setProduct(null);
      onAvailabilityChange?.(false);
    } catch (error) {
      console.error("Failed to fetch add-on data:", error);
      setProduct(null);
      onAvailabilityChange?.(false);
    }
  }, [computedSubtotal, onAvailabilityChange]);

  useEffect(() => {
    fetchAddOns();
  }, [fetchAddOns]);

  useEffect(() => {
    setAdded(false);
  }, [product?.productID]);

  const handleAddToCart = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (!product || added || adding) return;
    setAdding(true);

    const callback = (response: unknown) => {
      setAdding(false);
      if (response === "error") return;

      const responseData =
        typeof response === "object" && response !== null && "data" in response
          ? (response as { data?: { notice?: unknown } }).data
          : undefined;
      const notice =
        responseData?.notice ||
        (typeof response === "object" && response !== null && "notice" in response
          ? (response as { notice?: unknown }).notice
          : undefined);

      if (
        notice
        && typeof notice === "object"
        && !Array.isArray(notice)
        && Object.keys(notice).length > 0
      ) {
        const { title, content } = notice as { title?: string; content?: string };
        const message = [title, content].filter(Boolean).join("\n");
        if (message) toast.show(message, 4000);
      }

      setAdded(true);
      onAddOnSuccess?.(response);

      if (source === "detail") {
        window.location.href = "/view-cart";
      }
    };

    const productList = getAddOnCartItems(product.productID);
    if (productList.length > 1) {
      batchAddToCart(
        event,
        { productList },
        callback,
      );
    } else {
      addToCart(
        event,
        { productID: product.productID, qty: 1, from: 1 },
        callback,
      );
    }
  };

  if (!product) return null;

  const addonPrice = product.addonPrice ?? product.price;
  const originalPrice = product.normalPrice;
  const productImage = getImage(product);
  const productAdded = added || product.appSelectStatus === 1;

  return (
    <div>
      <div
        className={`${styles["comp-addon-deals"]} ${source === "cart" ? styles["cart-addon-deals"] : ""}`}
      >
        <div className={styles["header"]}>
          <div className={styles["header-left"]}>
            <span className={styles["title"]}>Super Value Add-on Deals</span>

            <Image
              src={`${process.env.IMAGE_DOMAIN}/svg/product-detail/icon-Info.svg`}
              alt="info"
              width={98}
              height={98}
              className={styles["info-icon"]}
              onClick={() => setShowInfoDialog(true)}
            />
          </div>
          <span
            className={styles["see-more"]}
            onClick={() => setShowProductList(true)}
          >
            See More &gt;
          </span>
        </div>

        <div className={styles["product-card"]}>
          <Link href={`${product.url}`}>
            <div className={styles["product-image"]}>
              <Image
                src={productImage}
                alt={product.name}
                width={98}
                height={98}
                className={styles["goods-image"]}
              />
            </div>
          </Link>

          <div className={styles["product-info"]}>
            <Link href={`${product.url}`}>
              <div className={styles["product-name"]}>{product.name}</div>
            </Link>
            <div className={styles["price-section"]}>
              <div className={styles["price-left"]}>
                <div className={styles["addon-price-label"]}>Add-on Price</div>
                <div className={styles["price-row"]}>
                  <span className={styles["sale-price"]}>
                    ${addonPrice.toFixed(2)}
                  </span>
                  {originalPrice > addonPrice && (
                    <span className={styles["original-price"]}>
                      ${originalPrice.toFixed(2)}
                    </span>
                  )}
                </div>
              </div>
              <div className={styles["add-btn"]} onClick={handleAddToCart}>
                <Image
                  src={`${process.env.IMAGE_DOMAIN}/svg/product-detail/${productAdded ? "icon-add-on-success" : "icon-add-on"}.svg`}
                  alt="add"
                  width={32}
                  height={32}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <Dialog
        visible={showInfoDialog}
        title=""
        closeable={true}
        onClose={() => setShowInfoDialog(false)}
        showCancelButton={false}
        showConfirmButton={false}
        dialogContentStyle={{ width: "calc(100vw - 18px)", maxWidth: 430 }}
      >
        <div style={{ maxHeight: "70vh", overflowY: "auto" }}>
          <AddOnInfoContent />
        </div>
      </Dialog>

      <AddOnProductList
        visible={showProductList}
        onClose={() => setShowProductList(false)}
        subtotal={source === "detail" ? computedSubtotal : undefined}
        mainProduct={mainProduct}
        onAddOnSuccess={onAddOnSuccess}
      />
    </div>
  );
}
